import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Typography, Form, Input, Button, notification, Spin } from 'antd';
import { PasswordInput } from "antd-password-input-strength";
import _service from '@netuno/service-client';

import { FormattedMessage, useIntl } from 'react-intl';

import Core from '../../common/Core';

import AddressFields from '../../components/AddressFields';

import './index.less';

const langContext = 'profile';

const { Title } = Typography;

export default function Profile(props) {

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [passwordRequired, setPasswordRequired] = useState(false);
    const [form] = Form.useForm();

    const location = useLocation();

    const layout = {
        wrapperCol: { xs: { span: 24 }, sm: { span: 24 }, md: { span: 24 }, lg: { span: 12 } }
    };

    const intl = useIntl();

    useEffect(() => {
        if (form)
            onFetchProfile();
    }, [location]);

    function onFetchProfile() {
        setLoading(true);
        _service({
            method: 'GET',
            url: 'client',
            success: (response) => {
                if (response.json.result) {
                    form.setFieldsValue({
                        name: response.json.data.name,
                        username: response.json.data.username,
                        mail: response.json.data.email,
                        postal_code: response.json.data.postal_code,
                        address_extra: response.json.data.address_extra,
                        nif: response.json.data.nif
                    });
                    setLoading(false);
                } else {
                    notification["warning"]({
                        message: 'Ocorreu um erro a carregar os dados',
                        description: response.json.error,
                    });
                    setLoading(false);
                }
            },
            fail: () => {
                setLoading(false);
                notification["error"]({
                    message: 'Ocorreu um erro a carregar os dados',
                    description: 'Ocorreu um erro a carregar os dados, por favor tente novamente.',
                });
            }
        });
    }

    function onFinish(values) {
        setSubmitting(true);
        const { name, username, password, mail, postal_code, address_extra, nif } = values;
        _service({
            method: 'PUT',
            url: 'client',
            data: {
                name,
                username,
                password,
                mail,
                postal_code,
                address_extra,
                nif,
                language_code: Core.currentLanguage()
            },
            success: (response) => {
                if (response.json.result) {
                    notification["success"]({
                        message: 'Edição do Perfil',
                        description: 'Os dados do seu perfil foram alterados com sucesso.',
                    });
                    setSubmitting(false);
                    form.setFieldsValue({
                        password: "",
                        password_confirm: ""
                    });
                } else {
                    notification["warning"]({
                        message: 'Utilizador existente',
                        description: response.json.error,
                    });
                    setSubmitting(false);
                    form.setFieldsValue({
                        password: "",
                        password_confirm: ""
                    });
                }
            },
            fail: () => {
                setSubmitting(false);
                notification["error"]({
                    message: 'Erro na Edição do Perfil',
                    description: 'Ocorreu um erro na edição do seu perfil, por favor contacte-nos através do chat de suporte.',
                });
            }
        });
    }

    function onValuesChange(changedValues, allValues) {
        if (allValues.password && allValues.password.length > 0) {
            setPasswordRequired(true);
        } else {
            setPasswordRequired(false);
        }
    };

    function onFinishFailed(errorInfo) {
        console.log('Failed:', errorInfo);
    }

    if (loading) {
        return (
            <div className="loading-wrapper">
                <div className="content-title">
                    <Title level={2}><Spin /> <FormattedMessage id={`${langContext}.fields.loading`} /></Title>
                </div>
            </div>
        );
    } else {
        return (
            <div>
                <div className="content-title">
                    <Button className="go-back-btn" type="link" onClick={() => props.history.goBack()}><ArrowLeftOutlined /> <FormattedMessage id={`${langContext}.fields.go-back`} /></Button>
                </div>
                <div className="content-title">
                    <Title level={2}><FormattedMessage id={`${langContext}.title`} /></Title>
                </div>
                <div className="content-body">
                    <Form
                        {...layout}
                        onValuesChange={onValuesChange}
                        form={form}
                        layout="vertical"
                        name="basic"
                        initialValues={{ remember: true }}
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                    >
                        <Form.Item
                            label={intl.formatMessage({ id: 'profile.fields.name' })}
                            name="name"
                            rules={[
                                { required: true, message: 'Insira o seu nome.' }
                            ]}
                        >
                            <Input disabled={submitting} maxLength={25} />
                        </Form.Item>
                        <AddressFields form={form} />
                        <Form.Item
                            label={intl.formatMessage({ id: 'profile.fields.email' })}
                            name="mail"
                            rules={[
                                { type: 'email', message: 'O e-mail inserido não é válido.' },
                                { required: true, message: 'Insira o e-mail.' }
                            ]}
                        >
                            <Input disabled={submitting} maxLength={250} />
                        </Form.Item>
                        <Form.Item
                            label={intl.formatMessage({ id: 'profile.fields.nif' })}
                            name="nif"
                            rules={[
                                { required: true, message: 'Insira o NIF.' }
                            ]}
                        >
                            <Input disabled={submitting} maxLength={250} />
                        </Form.Item>
                        <Form.Item
                            label={intl.formatMessage({ id: 'profile.fields.new-password' })}
                            name="password"
                            rules={[
                                { type: 'string', message: 'Palavra-Passe deverá ter entre 8 a 25 caracteres.', min: 8, max: 25 },
                            ]}
                        >
                            <PasswordInput />
                        </Form.Item>
                        <Form.Item
                            label={intl.formatMessage({ id: 'profile.fields.confirm-new-password' })}
                            name="password_confirm"
                            rules={[
                                { required: passwordRequired, message: 'Insira a confirmação da nova palavra-passe.' },
                                { type: 'string', message: 'Palavra-Passe deverá ter entre 8 a 25 caracteres.', min: 8, max: 25 },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject('As palavras-passes não são iguais.');
                                    },
                                })
                            ]}
                        >
                            <Input.Password maxLength={25} />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" loading={submitting}>
                                <FormattedMessage id={`${langContext}.button.update`} />
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        );
    }
}
